<script lang="ts">
	/**
	 * One money figure, rendered **once per currency** (Design §4, §10).
	 *
	 * Every monetary metric the store keeps arrives as a list keyed by currency, and it stays a
	 * list all the way to the screen. Adding €1,000 to ¥1,000 produces a number that is not money
	 * in any currency, and a single "total" is the one shape of this widget that would be wrong
	 * in a way nobody could see — so there is no total, and the caption says why.
	 *
	 * Formatting goes through `formatAmount`, which knows each currency's minor-unit digits. The
	 * fallback when a currency is not configured in the storefront reports the raw minor units and
	 * says so, because a client that quietly divides by 100 renders ¥1,200 as ¥12 and looks
	 * entirely plausible doing it.
	 */

	import { formatAmount } from '../stores/storefront';

	/** Eyebrow label for the figure. */
	export let label: string = '';
	/** `[{ currency, amount }]` in minor units, or `[{ currency, count }]`, or a rate per currency. */
	export let amounts: { currency: string; amount: number }[] = [];
	/** Longer explanation shown under the label. */
	export let description: string = '';
	/** Icon for the eyebrow, without the `fa-` prefix. */
	export let faIcon: string = '';
	/** Shown when the list is empty. */
	export let emptyText: string = 'Nothing recorded.';
	/** Draws attention without relying on colour alone — the caller also supplies the words. */
	export let tone: 'normal' | 'warning' = 'normal';

	function fnMoney(amount: number, currency: string): string {
		const formatted = $formatAmount(amount, currency);
		return formatted ?? `${amount} minor units`;
	}
</script>

<div class="widget bg-depth {tone === 'warning' ? 'border-widget border-amber-400 dark:border-amber-500' : ''}">
	<div class="card-label">
		{#if faIcon.length > 0}<i class="fa-solid fa-{faIcon} text-[11px] mr-1 text-default" />{/if}{label}
	</div>

	{#if amounts.length === 0}
		<div class="text-default text-sm mt-1">{emptyText}</div>
	{:else}
		<div class="mt-1 space-y-1">
			{#each amounts as entry (entry.currency)}
				<div class="flex items-baseline justify-between gap-3">
					<span class="text-default font-mono text-xs uppercase">{entry.currency}</span>
					<span class="text-lg font-semibold">{fnMoney(entry.amount, entry.currency)}</span>
				</div>
			{/each}
		</div>
	{/if}

	{#if description.length > 0}
		<p class="text-default text-xs mt-2">{description}</p>
	{:else if amounts.length > 1}
		<p class="text-default text-xs mt-2">One line per currency. These are not added together.</p>
	{/if}
</div>
